import type { IntegerAmount } from '@actual-app/core/shared/util';

import { LATON } from '#components/mobile/ayuda/OjoBuey';
import { color } from '#components/mobile/ui/tokens';

const ANCHO = 330;

/**
 * Ritmo del mes: lo gastado día a día (línea de mar, con el barquito en
 * «hoy») frente a lo normal para llegar al final del mes en la referencia
 * (línea de puntos de latón).
 */
export function LineaRitmo({
  acumulado,
  diasMes,
  referencia,
  fijos,
  alto = 120,
  etiquetaInicio,
  etiquetaHoy,
}: {
  /** Gasto acumulado hasta hoy (índice 0 = día 1). */
  acumulado: readonly IntegerAmount[];
  diasMes: number;
  referencia: IntegerAmount;
  fijos: IntegerAmount;
  alto?: number;
  etiquetaInicio: string;
  etiquetaHoy: string;
}) {
  const dia = Math.max(acumulado.length, 1);
  const gastado = acumulado[acumulado.length - 1] ?? 0;
  const tope = Math.max(referencia, gastado, 1) * 1.05;
  const x = (d: number) =>
    8 + ((d - 1) / Math.max(diasMes - 1, 1)) * (ANCHO - 16);
  const y = (v: number) => alto - 18 - (Math.max(v, 0) / tope) * (alto - 28);
  const fijo = Math.min(Math.max(fijos, 0), referencia);
  const puntos = acumulado.map((v, i) => `${x(i + 1)},${y(v)}`).join(' ');
  const bx = x(dia);
  const by = y(gastado);
  return (
    <svg
      viewBox={`0 0 ${ANCHO} ${alto}`}
      aria-hidden="true"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      data-testid="linea-ritmo"
    >
      <path
        d={`M${x(1)} ${alto - 18} L${puntos.split(' ').join(' L')} L${bx} ${alto - 18} Z`}
        fill={color.accent}
        opacity={0.1}
      />
      {referencia > 0 && (
        <line
          x1={x(1)}
          y1={y(fijo)}
          x2={x(diasMes)}
          y2={y(referencia)}
          stroke={LATON.oscuro}
          strokeWidth={2}
          strokeDasharray="4 5"
        />
      )}
      <polyline
        points={puntos}
        fill="none"
        stroke={color.fg}
        strokeWidth={2.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <text x={8} y={alto - 3} fontSize={11} fontWeight={600} fill={color.fg3}>
        {etiquetaInicio}
      </text>
      <text
        x={bx}
        y={alto - 3}
        textAnchor={dia > diasMes - 3 ? 'end' : 'middle'}
        fontSize={11}
        fontWeight={800}
        fill={color.fg}
      >
        {etiquetaHoy}
      </text>
      <g transform={`translate(${bx} ${by}) translate(-12 -24)`}>
        <path d="M2 17 H22 L18 22 H6 Z" fill={LATON.oscuro} />
        <path d="M12 2 V17" stroke={color.fg} strokeWidth={1.6} />
        <path
          d="M12.8 3 L21 15 H12.8 Z"
          fill="#fff"
          stroke={color.fg}
          strokeWidth={1.1}
        />
        <path
          d="M11.2 6 L5 15 H11.2 Z"
          fill={LATON.claro}
          stroke={color.fg}
          strokeWidth={1.1}
        />
      </g>
    </svg>
  );
}

/** Pares de barras (entró / salió) por mes, con la etiqueta del mes. */
export function BarrasPares({
  meses,
  colorA,
  colorB,
  alto = 70,
}: {
  meses: readonly { etiqueta: string; a: IntegerAmount; b: IntegerAmount }[];
  colorA: string;
  colorB: string;
  alto?: number;
}) {
  const ancho = meses.length <= 3 ? 150 : ANCHO;
  const tope = Math.max(1, ...meses.flatMap(m => [m.a, m.b])) * 1.08;
  const hueco = (ancho - 8) / Math.max(meses.length, 1);
  const barra = hueco * 0.3;
  const h = (v: number) => Math.max(1.5, (Math.max(v, 0) / tope) * (alto - 18));
  return (
    <svg
      viewBox={`0 0 ${ancho} ${alto}`}
      aria-hidden="true"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      data-testid="barras-pares"
    >
      {meses.map((m, i) => {
        const x0 = 4 + i * hueco + hueco * 0.18;
        return (
          <g key={`${m.etiqueta}-${i}`}>
            <rect
              x={x0}
              y={alto - 16 - h(m.a)}
              width={barra}
              height={h(m.a)}
              rx={3}
              fill={colorA}
            />
            <rect
              x={x0 + barra + 2}
              y={alto - 16 - h(m.b)}
              width={barra}
              height={h(m.b)}
              rx={3}
              fill={colorB}
            />
            <text
              x={x0 + barra + 1}
              y={alto - 3}
              textAnchor="middle"
              fontSize={10.5}
              fontWeight={600}
              fill={color.fg3}
            >
              {m.etiqueta}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Línea sencilla de una serie, con el último punto marcado. */
export function LineaMini({
  valores,
  trazo,
  ancho = ANCHO,
  alto = 44,
}: {
  valores: readonly number[];
  trazo: string;
  ancho?: number;
  alto?: number;
}) {
  if (valores.length < 2) {
    return null;
  }
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const rango = max - min || 1;
  const x = (i: number) => 4 + (i / (valores.length - 1)) * (ancho - 8);
  const y = (v: number) => alto - 6 - ((v - min) / rango) * (alto - 14);
  const ultimo = valores.length - 1;
  return (
    <svg
      viewBox={`0 0 ${ancho} ${alto}`}
      aria-hidden="true"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      data-testid="linea-mini"
    >
      <polyline
        points={valores.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
        fill="none"
        stroke={trazo}
        strokeWidth={2.4}
        strokeLinejoin="round"
      />
      <circle cx={x(ultimo)} cy={y(valores[ultimo])} r={3.6} fill={trazo} />
    </svg>
  );
}
